import { apiSlice } from './apiSlice';

/**
 * Profile → 2FA. Three calls, in the order the section walks them:
 *
 *   GET  /auth/2fa/status/  → { success, is_2fa_enabled }
 *   POST /auth/2fa/setup/   → { success, totp_uri, secret, temp_secret, message }
 *   POST /auth/2fa/enable/  ← { otp_code, temp_secret }
 *   POST /auth/2fa/disable/ ← { otp_code }
 *
 * The verbs are the ones the backend advertises in its `Allow` header, which is
 * why setup is a mutation despite reading like a fetch: it mints a new temp
 * secret every call, so it must not be cached or replayed on a refocus.
 *
 * `temp_secret` is the handle enable() is checked against — it carries the
 * pending secret until the first valid code proves the app was really enrolled,
 * so it lives in the section's own state between the two calls rather than
 * here. Nothing about the pending secret is worth caching.
 */
export const twoFactorApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    get2faStatus: builder.query({
      query: () => '/auth/2fa/status/',
      transformResponse: (response) => Boolean(response?.is_2fa_enabled),
      providesTags: ['Auth'],
    }),

    setup2fa: builder.mutation({
      query: () => ({ url: '/auth/2fa/setup/', method: 'POST' }),
    }),

    enable2fa: builder.mutation({
      query: ({ otpCode, tempSecret }) => ({
        url: '/auth/2fa/enable/',
        method: 'POST',
        body: { otp_code: otpCode, temp_secret: tempSecret },
      }),
      // The status query is the one thing that must not stay stale afterwards.
      invalidatesTags: ['Auth'],
    }),

    /**
     * Turning it off asks for a live code as well: the point of 2FA is that a
     * stolen password can't undo it. The endpoint is POST-only like the others;
     * `otp_code` is the field name the enable call uses, so it is the field
     * name sent here — confirm against the backend if disable ever 400s.
     */
    disable2fa: builder.mutation({
      query: ({ otpCode }) => ({
        url: '/auth/2fa/disable/',
        method: 'POST',
        body: { otp_code: otpCode },
      }),
      invalidatesTags: ['Auth'],
    }),
  }),
});

export const {
  useGet2faStatusQuery,
  useSetup2faMutation,
  useEnable2faMutation,
  useDisable2faMutation,
} = twoFactorApi;
